import { Container, Grid } from '@mui/material';
import { get } from 'lodash';
import React from 'react';

import Folded2025Card from '~components/business-owner/Folded2025/components/Folded2025Card';
import { useFolded2025Data } from '~components/business-owner/Folded2025/hooks/useFolded2025Data';

import { formatCurrency } from '~utils/functions';

import styles from './Folded2025Revenue.module.scss';

import FallIcon from '../../assets/icons/Fall.svg?react';
import FlameIcon from '../../assets/icons/Flame.svg?react';
import MoonIcon from '../../assets/icons/Moon.svg?react';
import SpringIcon from '../../assets/icons/Spring.svg?react';
import SummerIcon from '../../assets/icons/Sun.svg?react';
import WinterIcon from '../../assets/icons/Winter.svg?react';
import BottomLeft from '../../assets/Revenue_BottomLeft.svg?react';
import BottomRight from '../../assets/Revenue_BottomRight.svg?react';
import RevenueIcon from '../../assets/Revenue_Icon.svg?react';
import ArrowIcon from '../../assets/Revenue_InlineIcon.svg?react';
import TopLeft from '../../assets/Revenue_TopLeft.svg?react';
import TopRight from '../../assets/Revenue_TopRight.svg?react';
import Folded2025Title from '../Folded2025Title';

const Folded2025Revenue = (): JSX.Element => {
	const { data } = useFolded2025Data();

	const getSeasonIcon = (month: number): React.ReactNode => {
		if (month >= 2 && month <= 4) {
			return <SpringIcon />;
		}
		if (month >= 5 && month <= 7) {
			return <SummerIcon />;
		}
		if (month >= 8 && month <= 10) {
			return <FallIcon />;
		}
		return <WinterIcon />;
	};

	const getTimeIcon = (hour: number): React.ReactNode => {
		if (hour >= 18 || hour <= 6) {
			return <MoonIcon />;
		} else {
			return <SummerIcon />;
		}
	};

	const seasonIcon = getSeasonIcon(
		data?.summary.revenue?.best_month.month
			? new Date(data.summary.revenue.best_month.month).getMonth()
			: 0,
	);

	const timeIcon = getTimeIcon(get(data, 'summary.revenue.peak_hour.hour', 0));

	const formatHour = (hour: number | undefined): string => {
		if (hour === undefined) {
			return 'N/A';
		}
		const period = hour >= 12 ? 'PM' : 'AM';
		const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
		return `${displayHour}:00 ${period}`;
	};

	return (
		<div className={styles.container}>
			<TopLeft className={styles.topLeftDecoration} />
			<TopRight className={styles.topRightDecoration} />

			<BottomLeft className={styles.bottomLeftDecoration} />
			<BottomRight className={styles.bottomRightDecoration} />

			<Container disableGutters>
				<Folded2025Title icon={<RevenueIcon />}>Revenue</Folded2025Title>

				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<Folded2025Card>
							<Folded2025Card.Label>
								Your Total Revenue
							</Folded2025Card.Label>
							<Folded2025Card.Value size="xxl">
								{formatCurrency(
									data?.summary?.revenue?.total_revenue,
									false,
								)}
							</Folded2025Card.Value>
							{(data?.summary?.revenue?.yoy_growth_percentage || 0) > 0 && (
								<Folded2025Card.Subtext>
									<Folded2025Card.SubtextIcon>
										<ArrowIcon />
									</Folded2025Card.SubtextIcon>
									{data?.summary?.revenue?.yoy_growth_percentage}%
								</Folded2025Card.Subtext>
							)}
						</Folded2025Card>
					</Grid>
					<Grid item xs={12} md={6} container spacing={2}>
						<Grid item xs={12}>
							<Folded2025Card icon={seasonIcon}>
								<Folded2025Card.Label>
									Your top performing month in revenue
								</Folded2025Card.Label>
								<Folded2025Card.Value>
									{data?.summary?.revenue?.best_month.month}
								</Folded2025Card.Value>
								<Folded2025Card.Label>
									{formatCurrency(
										data?.summary?.revenue?.best_month.revenue,
									)}
								</Folded2025Card.Label>
							</Folded2025Card>
						</Grid>
						<Grid item xs={12}>
							<Folded2025Card icon={timeIcon}>
								<Folded2025Card.Label>
									Highest revenue-producing hour of the day
								</Folded2025Card.Label>
								<Folded2025Card.Value>
									{formatHour(data?.summary?.revenue?.peak_hour?.hour)}
								</Folded2025Card.Value>
							</Folded2025Card>
						</Grid>
						<Grid item xs={12}>
							<Folded2025Card icon={<FlameIcon />}>
								<Folded2025Card.Label>
									Longest streak of revenue growth
								</Folded2025Card.Label>
								<Folded2025Card.Value>
									{data?.summary?.revenue?.longest_growth_streak
										?.weeks || 'N/A'}{' '}
									weeks
								</Folded2025Card.Value>
							</Folded2025Card>
						</Grid>
					</Grid>
				</Grid>
			</Container>
		</div>
	);
};

export default Folded2025Revenue;
